import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { treatmentsApi, skinConditions } from '../services/api';
import { Clock, DollarSign, Sparkles, Loader2, MessageCircle } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

const Treatments = () => {
  const { branding } = useBranding();
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [recommendedTreatments, setRecommendedTreatments] = useState([]);

  // Function to open chatbot
  const openChatbot = () => {
    const chatbotToggle = document.querySelector('[data-testid="chatbot-toggle"]');
    if (chatbotToggle) {
      chatbotToggle.click();
    }
  };

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      const data = await treatmentsApi.getAll();
      setTreatments(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching treatments:', err);
      setError('Failed to load treatments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConditionSelect = (conditionId) => {
    setSelectedCondition(conditionId);
    
    // Filter treatments based on selected skin condition
    const recommended = treatments.filter(treatment =>
      treatment.skinConditions && treatment.skinConditions.includes(conditionId)
    );
    setRecommendedTreatments(recommended);
  };

  const displayTreatments = selectedCondition ? recommendedTreatments : treatments;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-lg">Loading treatments...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex flex-col items-center justify-center py-32">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchTreatments}>Try Again</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Treatments
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover personalized aesthetic treatments designed to help you look and feel your best
          </p>
        </div>

        {/* Skin Condition Selector */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            Find treatments for your skin concerns
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant={selectedCondition === null ? 'default' : 'outline'}
              onClick={() => {
                setSelectedCondition(null);
                setRecommendedTreatments([]);
              }}
            >
              All Treatments
            </Button>
            {skinConditions.map((condition) => (
              <Button
                key={condition.id}
                variant={selectedCondition === condition.id ? 'default' : 'outline'}
                onClick={() => handleConditionSelect(condition.id)}
              >
                {condition.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Treatments Grid */}
        {displayTreatments.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayTreatments.map((treatment) => (
              <Card key={treatment.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={treatment.image}
                    alt={treatment.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl">{treatment.name}</CardTitle>
                    <Badge variant="secondary" className="bg-secondary text-primary">
                      {treatment.category}
                    </Badge>
                  </div>
                  <CardDescription className="text-base">
                    {treatment.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 mr-1" />
                      <span>${treatment.price} {treatment.priceUnit}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{treatment.duration} min</span>
                    </div>
                  </div>
                  <Button className="w-full" asChild>
                    <a href="#book-now">Book Now</a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Sparkles className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600">No treatments found for this condition</p>
          </div>
        )}

        {/* AI Advisor CTA */}
        <div className="mt-16 bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 text-center text-white">
          <MessageCircle className="h-12 w-12 mx-auto mb-4 text-primary" />
          <h2 className="text-3xl font-bold mb-4">
            Not sure which treatment is right for you?
          </h2>
          <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
            Chat with {branding.chatbotName || 'our AI advisor'} for personalized recommendations based on your skin goals
          </p>
          <Button 
            size="lg" 
            onClick={openChatbot}
            className="bg-primary text-gray-900 hover:bg-primary/90"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Talk to {branding.chatbotName || 'AI Advisor'}
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Treatments;